import * as utils from '@utils/form';
import { RELATED_CONTENT_TYPE } from '@constants/relatedContentModules';
import { IMAGE_SIZE } from '@api/media/constants';
import {
  WA_WHITE,
  GRADIENTS,
  SLUG_RULE,
  TITLE_RULE,
  TEXT_COLOURS,
  OREGON_COLOURS,
  SPIKES_COLOURS,
  BRANDED_COLOURS,
} from '@constants';

export const FORM_FIELD = {
  TYPE: {
    name: 'type',
    label: 'Article type:',
    rules: [{ required: true, message: 'Article type is required' }],
  },
  BACKGROUND_COLOUR: {
    name: 'backgroundColour',
    label: 'Article background colour:',
    colors: [...TEXT_COLOURS],
    rules: [{ required: true, message: 'Article background colour is required' }],
  },
  RELATED_ARTICLES_BACKGROUND_COLOUR: {
    name: 'relatedArticlesBackgroundColour',
    label: 'Related articles background colour:',
    colors: [...BRANDED_COLOURS, WA_WHITE, ...OREGON_COLOURS, ...SPIKES_COLOURS, GRADIENTS['gradient-1'].key],
    rules: [{ required: true, message: 'Related articles background colour is required' }],
  },
  LAYOUT: {
    name: 'columns',
    label: 'Article layout:',
    rules: [{ required: true, message: 'Layout is required' }],
  },
  EVENT: {
    name: 'event',
    label: 'Link to an event:',
    rules: [{ required: true, message: 'Event is required' }],
    serialize: utils.relatedEntitySerializer,
    deserialize: utils.relatedEntityDeserializer,
    deserializeError: utils.deserializeErrorRelatedField,
  },
  HIDE_ON_MAIN_SITE: {
    name: 'hideOnTheMainSite',
    label: 'Hide on the main site',
  },
  CHAMPIONSHIP_ARTICLE: {
    name: 'championshipArticle',
    label: 'Championship Article',
  },
  TITLE: {
    name: 'title',
    label: 'Article title:',
    rules: [{ required: true, message: 'Title is required' }, TITLE_RULE],
  },
  SLUG: {
    name: 'slug',
    label: 'Slug:',
    rules: [{ required: true, message: 'Page slug is required' }, SLUG_RULE],
  },
  STANDFIRST: {
    name: 'standFirst',
    label: 'Standfirst (short intro):',
    rules: [{ required: true, message: 'Standfirst is required' }],
  },
  FEATURE_IMAGE: {
    name: 'featureImage',
    label: 'Feature image:',
    rules: [{ required: true, message: 'Feature image is required' }],
    size: IMAGE_SIZE['2000x714'],
    serialize: utils.imageSerialize,
    deserialize: utils.imageDeserialize,
    deserializeError: utils.deserializeErrorRelatedField,
  },
  BODY: {
    name: 'body',
    label: 'Article body:',
    rules: [{ required: true, message: 'Body is required' }],
  },
  SEO_TITLE: {
    name: 'seoTitle',
    label: 'Seo title:',
    rules: [
      { required: true, message: 'Seo title is required' },
      { max: 60, message: 'SEO title should be less than 60 chars' },
    ],
  },
  SEO_DESCRIPTION: {
    name: 'seoDescription',
    label: 'SEO description:',
    rules: [{ required: true, message: 'SEO description is required' }],
  },
  GATED_CONTENT: {
    name: 'gatedContent',
    label: 'Gated content',
  },
  CAMPAIGN: {
    name: 'campaign',
    label: 'Campaign',
    placeholder: 'Add new campaign',
    rules: [{ required: true, message: 'Campaign is required' }],
    serialize: utils.relatedEntitySerializer,
    deserialize: utils.relatedEntityDeserializer,
    deserializeError: utils.deserializeErrorRelatedField,
  },
  RELATED_ATHLETES: {
    name: 'relatedAthlete',
    type: RELATED_CONTENT_TYPE.ATHLETES,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_CHAMPIONSHIP_EVENTS: {
    name: 'relatedChampionshipEvents',
    type: RELATED_CONTENT_TYPE.CHAMPIONSHIP_EVENTS,
  },
  RELATED_DISCIPLINES: {
    name: 'relatedDiscipline',
    type: RELATED_CONTENT_TYPE.DISCIPLINES,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_COMPETITIONS: {
    name: 'relatedCompetition',
    type: RELATED_CONTENT_TYPE.COMPETITIONS,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_ARTICLES: {
    name: 'relatedArticle',
    type: RELATED_CONTENT_TYPE.ARTICLES,
    serialize: utils.relatedContentSerializerForContentIdOrId,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_EVENTS: {
    name: 'relatedEvent',
    type: RELATED_CONTENT_TYPE.EVENTS,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_MINISITES: {
    name: 'relatedMinisite',
    type: RELATED_CONTENT_TYPE.MINISITES,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_COUNTRIES: {
    name: 'relatedCountry',
    type: RELATED_CONTENT_TYPE.COUNTRIES,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_REGIONS: {
    name: 'relatedRegion',
    type: RELATED_CONTENT_TYPE.REGIONS,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  RELATED_URLS: {
    name: 'relatedUrls',
    type: RELATED_CONTENT_TYPE.URLS,
  },
  RELATED_LOCATIONS: {
    name: 'relatedLocation',
    type: RELATED_CONTENT_TYPE.LOCATIONS,
    serialize: utils.relatedContentSerializer,
    deserializeError: utils.deserializeErrorRelatedContent,
  },
  ARTICLE_LANGUAGE: {
    name: 'language',
  },
  TAGS: {
    name: 'tags',
    type: RELATED_CONTENT_TYPE.TAGS,
  },
  USER_CATEGORIES: {
    name: 'userCategories',
    type: RELATED_CONTENT_TYPE.USER_CATEGORIES,
  },
  // TODO: implement when API is ready
  // RELATED_DOCUMENTS: {
  //   name: 'relatedDocuments',
  // type: RELATED_CONTENT_TYPE.DOCUMENTS,
  // serialize: utils.relatedContentSerializer,
  // },
  CONTENT_MODULES: {
    label: 'Content modules',
    name: 'contentModules',
    preset: 'articles',
    deserializeError: utils.deserializeContentModulesErrors,
  },
};

export const FORM_FIELDS = Object.values(FORM_FIELD);

export const DEFAULT_VALUES = {
  [FORM_FIELD.CONTENT_MODULES.name]: [],
};

export const FORM_VALUES_TO_WATCH = [FORM_FIELD.TYPE.name, FORM_FIELD.LAYOUT.name, FORM_FIELD.EVENT.name];
